import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { onAuthStateChanged, signOut, User as FirebaseUser } from 'firebase/auth';
import { auth } from '@/shared/lib/firebase';
import { apolloClient } from '@/shared/lib/api/apollo';
import { INTERNAL_SIGN_IN, VIEWER_PROFILE } from '../api/auth.queries';
import { logger } from '@/shared/lib/logger';
import { AuthContextValue } from './auth.types';
import {
  UserContext,
  Role,
  AccountType,
  InternalUserStatus,
} from '@/entities/user/model/user.types';

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserContext | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refreshProfile = async () => {
    try {
      const { data } = await apolloClient.query({
        query: VIEWER_PROFILE,
        fetchPolicy: 'network-only',
      });
      if (!data?.viewer) throw new Error('Empty refresh response');
      const userProfile = data.viewer;
      const uid = auth.currentUser?.uid;
      if (!uid) throw new Error('Not signed in');

      setUser({
        uid,
        email: userProfile.email,
        firstName: userProfile.firstName ?? undefined,
        lastName: userProfile.lastName ?? undefined,
        role: userProfile.role as unknown as Role,
        accountType: userProfile.accountType as unknown as AccountType,
        internalStatus: userProfile.internalStatus as unknown as InternalUserStatus,
        permissions: userProfile.permissions || [],
        isAuthenticated: true,
      });

      logger.info('Profile refreshed successfully');
    } catch (err) {
      logger.error('Failed to refresh profile', err as Error);
      throw err;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      setIsLoading(true);
      try {
        if (firebaseUser) {
          await firebaseUser.getIdToken();
          logger.info('User authenticated', { uid: firebaseUser.uid });

          /*
           * `internalSignIn` rather than `authProfile`, and it is the only
           * caller of it.
           *
           * The Google popup fires `onAuthStateChanged` the moment it resolves,
           * before anything else can run. A first-time staff member has no
           * `users` row at that instant, so reading the profile would 404 and
           * bounce them — and having the login form provision separately just
           * races this listener to the same row. One idempotent mutation, called
           * from the one place that always runs, removes both problems: it
           * creates the row if it is missing, refuses the account if it may not
           * be staff, and returns the profile either way.
           *
           * A failure signs the user out. This used to fall back to the token's
           * own claims and set `isAuthenticated: true` regardless — a missing
           * row, a 403, the API being down — which rendered the whole admin
           * shell for anyone holding any Firebase session, a site customer's
           * included. The approval gate below is only worth something if failing
           * to read the status is a closed door.
           */
          const { data } = await apolloClient.mutate({ mutation: INTERNAL_SIGN_IN });
          if (!data?.internalSignIn) throw new Error('Empty profile response');
          const userProfile = data.internalSignIn;

          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email || '',
            firstName: userProfile.firstName ?? undefined,
            lastName: userProfile.lastName ?? undefined,
            role: userProfile.role as unknown as Role,
            accountType: userProfile.accountType as unknown as AccountType,
            internalStatus: userProfile.internalStatus as unknown as InternalUserStatus,
            permissions: userProfile.permissions || [],
            isAuthenticated: true,
          });
        } else {
          setUser(null);
          logger.info('User signed out');
        }
      } catch (err) {
        logger.error('Auth state change error', err as Error);
        setError(err as Error);
        setUser(null);
        // Leaving the Firebase session alive would have `onAuthStateChanged`
        // retry this on every mount and fail the same way, with the login page
        // insisting the user is already signed in.
        await signOut(auth).catch(() => undefined);
      } finally {
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, isLoading, error, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
